export type MultimodalReferenceKind = 'image' | 'video' | 'audio';

const TOKEN_KIND_LABELS = {
  image: 'Image',
  video: 'Video',
  audio: 'Audio',
} as const;

export const MULTIMODAL_REFERENCE_TOKEN_PATTERN =
  /\{\{(Image|Video|Audio)([1-9]\d*)\}\}/g;

export type MultimodalReferenceToken = {
  token: string;
  kind: MultimodalReferenceKind;
  number: number;
  start: number;
  end: number;
};

function parseKind(value: string): MultimodalReferenceKind {
  return value.toLowerCase() as MultimodalReferenceKind;
}

export function getMultimodalReferenceToken(
  kind: MultimodalReferenceKind,
  index: number
) {
  return `{{${TOKEN_KIND_LABELS[kind]}${index + 1}}}`;
}

export function getMultimodalReferenceLabel(
  kind: MultimodalReferenceKind,
  index: number
) {
  return `${TOKEN_KIND_LABELS[kind]} ${index + 1}`;
}

export function readMultimodalReferenceTokens(
  prompt: string
): MultimodalReferenceToken[] {
  return Array.from(prompt.matchAll(MULTIMODAL_REFERENCE_TOKEN_PATTERN)).map(
    (match) => {
      const start = match.index ?? 0;
      return {
        token: match[0],
        kind: parseKind(match[1]!),
        number: Number(match[2]),
        start,
        end: start + match[0].length,
      };
    }
  );
}

export function countMultimodalReferenceTokens(prompt: string) {
  const counts = new Map<string, number>();
  for (const reference of readMultimodalReferenceTokens(prompt)) {
    const key = `${reference.kind}:${reference.number}`;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

export function hasMalformedMultimodalReferenceToken(prompt: string) {
  const withoutValidTokens = prompt.replace(
    MULTIMODAL_REFERENCE_TOKEN_PATTERN,
    ''
  );
  return /\{\{\s*(?:image|video|audio)[^{}]*\}\}/i.test(withoutValidTokens);
}

export function parseMultimodalReferenceCountKey(key: string) {
  const [kind, rawNumber] = key.split(':');
  if (
    (kind !== 'image' && kind !== 'video' && kind !== 'audio') ||
    !rawNumber
  ) {
    return null;
  }
  const number = Number(rawNumber);
  if (!Number.isSafeInteger(number) || number < 1) return null;
  return { kind, number } as const;
}

export function reindexMultimodalReferencesAfterRemoval(
  prompt: string,
  kind: MultimodalReferenceKind,
  removedIndex: number
) {
  const removedNumber = removedIndex + 1;
  return prompt
    .replace(
      MULTIMODAL_REFERENCE_TOKEN_PATTERN,
      (token, rawKind: string, rawNumber: string) => {
        const tokenKind = parseKind(rawKind);
        if (tokenKind !== kind) return token;
        const number = Number(rawNumber);
        if (number === removedNumber) return '';
        return number > removedNumber
          ? getMultimodalReferenceToken(kind, number - 2)
          : token;
      }
    )
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/,\s*,/g, ',')
    .replace(/ +([,.;!?])/g, '$1');
}
